import { DrawerNavigationProp } from "@react-navigation/drawer";
import { MainStackParamList } from "../../navigation/Navigation.types";
import { useCallback, useEffect, useState } from "react";
import { store, useAlert, useAppDispatch } from "@grassrootly/rn-core";
import { usePlayerGroup, usePlayerGroups } from "../../api/groups/GroupsSelector";
import useBaseRequest from "../../api/BaseRequest";
import { CreateGame, GetGroupById, GetPlayerGroups, IPlayerGroup } from "@grassrootly/api";
import { useFocusEffect } from "@react-navigation/native";
import { IGameForm, GameType } from "./NewGame.types";
import { groupsActions } from "../../api/groups/GroupsReducer";
import { useSeasonSelector } from "./useSeasonSelector";

export function useNewGame({
  navigation,
  groupId,
}: {
  navigation: DrawerNavigationProp<MainStackParamList, "new-game", undefined>;
  groupId?: number;
}) {
  const { danger, success } = useAlert();
  const group = usePlayerGroup(groupId || 0);
  const playerGroups = usePlayerGroups();
  const dispatch = useAppDispatch();
  const [gameType, setGameType] = useState<GameType>(GameType.TRAINING);
  const [formLoading, setFormLoading] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(groupId || null);
  
  // Get admin groups (groups where user is owner)
  const adminGroups = playerGroups?.filter(g => g.is_owner) || [];

  // Auto-select group if user is admin of only one group
  useEffect(() => {
    if (!groupId && adminGroups.length === 1 && !selectedGroupId) {
      setSelectedGroupId(adminGroups[0].id);
    }
  }, [adminGroups, groupId, selectedGroupId]);
  
  // Season selector integration
  const { seasonOptions, selectedSeasonId, setSelectedSeasonId, selectedOrganizationId, loadingSeasons } = useSeasonSelector(selectedGroupId || undefined);
  const { loading, execute } = useBaseRequest(GetGroupById, {
    onError: (error) => {
      danger({ message: error?.message || "Something went wrong" });
      navigation.canGoBack() ? navigation.goBack() : navigation.navigate("my-groups");
    },
    onCompleted(data) {
      if (data) dispatch(groupsActions.setPlayerGroup(data));
    },
  });

  // Fetch player groups if no groupId is provided
  const { execute: fetchPlayerGroups } = useBaseRequest(GetPlayerGroups, {
    onCompleted(data) {
      if (data) {
        dispatch(groupsActions.setPlayerGroups(data));
      }
    },
  });

  const { loading: creating, execute: create } = useBaseRequest(CreateGame, {
    onCompleted(data, payload) {
      if (!data) return;
      success({ title: "Success", message: "Game successfully created!" });
      navigation.reset({
        index: 1,
        routes: [{ name: "my-groups" }, { name: "game", params: { id: data.id } }],
      });
    },
  });

  const getGroup = useCallback(() => {
    if (!selectedGroupId) return;
    const playerId = store.getState().auth?.user?.id || 0;
    execute({ id: selectedGroupId, playerId });
  }, [selectedGroupId, execute]);

  const onCreateGame = useCallback(
    (form: IGameForm) => {
      if (!selectedGroupId) {
        danger({ message: "Please select a group first" });
        return;
      }
      const selectedGroup = adminGroups.find(g => g.id === selectedGroupId);
      create({ 
        ...form, 
        groupId: selectedGroupId, 
        gameType: gameType, 
        groupName: selectedGroup?.name,
        seasonId: selectedSeasonId || null,
        organizationId: selectedOrganizationId || null
      });
    },
    [selectedGroupId, gameType, adminGroups, selectedSeasonId, selectedOrganizationId, create, danger]
  );

  useFocusEffect(useCallback(() => {
    const playerId = store.getState().auth?.user?.id || 0;
    if (groupId) {
      execute({ id: groupId, playerId });
    } else {
      // Fetch player groups to show group selector
      fetchPlayerGroups({ playerId });
    }
  }, [groupId]));
  return { 
    group, 
    loading, 
    creating, 
    onCreateGame, 
    gameType, 
    setGameType, 
    formLoading, 
    setFormLoading,
    seasonOptions,
    selectedSeasonId,
    setSelectedSeasonId,
    loadingSeasons,
    adminGroups,
    selectedGroupId,
    setSelectedGroupId,
    showGroupSelector: !groupId && adminGroups.length > 0
  };
}
